import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, CheckCircle2, AlertCircle, Copy, Key, Smartphone, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import QRCode from 'qrcode';
import { safeLog } from '@/utils/safeLogger';
import { Switch } from '@/components/ui/switch';

export function AdminSecuritySettings() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isHashingPassword, setIsHashingPassword] = useState(false);
  const [passwordHash, setPasswordHash] = useState<string | null>(null);
  
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [testCode, setTestCode] = useState('');
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  const [isGateEnabled, setIsGateEnabled] = useState(false);
  const [isLoadingGateStatus, setIsLoadingGateStatus] = useState(true);
  const [isTogglingGate, setIsTogglingGate] = useState(false);
  
  const { toast } = useToast();

  // Load current gate status
  useEffect(() => {
    const loadGateStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('global_settings')
          .select('enable_security_gate')
          .single();
        
        if (error) {
          console.error('[AdminSecuritySettings] Load error', error);
          setIsGateEnabled(false);
          return;
        }
        
        setIsGateEnabled(data?.enable_security_gate ?? false);
      } catch (error) {
        safeLog.error('[AdminSecuritySettings] Load gate status error', error);
      } finally {
        setIsLoadingGateStatus(false);
      }
    };
    loadGateStatus();
  }, []);

  const handleToggleGate = async () => {
    setIsTogglingGate(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-toggle-security-gate', {
        body: { enable: !isGateEnabled }
      });
      
      if (error) {
        throw new Error(error.message || 'Failed to toggle security gate');
      }
      
      if (!data?.success) {
        throw new Error(data?.error || 'Toggle failed without error details');
      }
      
      setIsGateEnabled(!isGateEnabled);
      
      toast({
        title: !isGateEnabled ? 'Security Gate Enabled' : 'Security Gate Disabled',
        description: !isGateEnabled 
          ? 'All users must now authenticate with password + 2FA. Refresh to apply changes.'
          : 'All users can now access the app without authentication. Refresh to apply changes.',
      });
      
      console.log('[Toggle] Success', data);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle security gate';
      console.error('[Toggle] Error', error);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsTogglingGate(false);
    }
  };

  const handleGeneratePasswordHash = async () => {
    if (password !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 12) {
      toast({
        title: 'Error',
        description: 'Password must be at least 12 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsHashingPassword(true);
    try {
      const { data, error } = await supabase.functions.invoke('hash-admin-password', {
        body: { password }
      });

      if (error) throw error;

      setPasswordHash(data.hash);
      toast({
        title: 'Success',
        description: 'Password hash generated. Copy and add to Lovable Cloud Secrets.',
      });
    } catch (error) {
      safeLog.error('[AdminSecuritySettings] Password hash error', error as Error);
      toast({
        title: 'Error',
        description: 'Failed to generate password hash',
        variant: 'destructive',
      });
    } finally {
      setIsHashingPassword(false);
      setPassword('');
      setConfirmPassword('');
    }
  };

  const handleGenerateTOTPSecret = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-admin-totp-secret');

      if (error) throw error;

      const secret = data.secret;
      setTotpSecret(secret);

      // Generate QR Code
      const otpauthUrl = `otpauth://totp/Email2Contact?secret=${secret}&issuer=Email2Contact`;
      const qrDataUrl = await QRCode.toDataURL(otpauthUrl, {
        width: 256,
        margin: 2,
      });
      setQrCodeUrl(qrDataUrl);

      toast({
        title: 'Success',
        description: 'TOTP Secret generated. Scan QR code with your authenticator app.',
      });
    } catch (error) {
      safeLog.error('[AdminSecuritySettings] TOTP generation error', error as Error);
      toast({
        title: 'Error',
        description: 'Failed to generate TOTP secret',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTestTOTP = async () => {
    if (!totpSecret || testCode.length !== 6) return;

    setIsTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-admin-totp', {
        body: { secret: totpSecret, code: testCode }
      });

      if (error) throw error;

      if (data.valid) {
        setTestResult('success');
        toast({
          title: 'Success',
          description: 'Code is valid! ✓',
        });
      } else {
        setTestResult('error');
        toast({
          title: 'Error',
          description: 'Invalid code. Try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      safeLog.error('[AdminSecuritySettings] TOTP test error', error as Error);
      setTestResult('error');
      toast({
        title: 'Error',
        description: 'Failed to test code',
        variant: 'destructive',
      });
    } finally {
      setIsTesting(false);
      setTestCode('');
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: `${label} copied to clipboard`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Security Gate Toggle */}
      <Card className="app-tile app-tile-hover border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="app-icon-box">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="app-section-title">Global Security Gate</CardTitle>
                <CardDescription>
                  Enable/disable Password + 2FA requirement for ALL users
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={isGateEnabled}
              onCheckedChange={handleToggleGate}
              disabled={isLoadingGateStatus || isTogglingGate}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Alert className={isGateEnabled ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"}>
            <AlertCircle className={`h-4 w-4 ${isGateEnabled ? "text-success" : "text-warning"}`} />
            <AlertDescription>
              {isGateEnabled ? (
                <>
                  <p className="font-semibold text-success mb-1">
                    🔒 Security Gate is ENABLED
                  </p>
                  <p className="text-sm text-muted-foreground">
                    ALL users must authenticate with master password + 2FA code to access the app.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-warning mb-1">
                    🔓 Security Gate is DISABLED
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Anyone can access the app without additional authentication.
                  </p>
                </>
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Password Configuration */}
      <Card className="app-tile app-tile-hover">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box">
              <Key className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="app-section-title">Master Password Configuration</CardTitle>
              <CardDescription>
                Generate a bcrypt hash for the admin master password
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              After generating the hash, manually add it to Lovable Cloud Secrets as <code className="font-mono text-sm bg-muted px-1 py-0.5 rounded">ADMIN_MASTER_PASSWORD_HASH</code>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="password">New Password (min. 12 characters)</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <Button 
            onClick={handleGeneratePasswordHash}
            disabled={!password || !confirmPassword || isHashingPassword}
            className="w-full"
          >
            {isHashingPassword ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate Password Hash'
            )}
          </Button>

          {passwordHash && (
            <Alert className="bg-success/10 dark:bg-success/20 border-success/30">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertDescription className="space-y-2">
                <p className="text-foreground font-medium">Hash generated successfully!</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-background border border-border p-2 rounded overflow-x-auto text-foreground">
                    {passwordHash}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(passwordHash, 'Password hash')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* TOTP Configuration */}
      <Card className="app-tile app-tile-hover">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="app-icon-box">
              <Smartphone className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="app-section-title">Two-Factor Authentication Setup</CardTitle>
              <CardDescription>
                Generate and test TOTP secret for admin authentication
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              After testing the code, manually add the secret to Lovable Cloud Secrets as <code className="font-mono text-sm bg-muted px-1 py-0.5 rounded">ADMIN_TOTP_SECRET</code>
            </AlertDescription>
          </Alert>

          <Button
            onClick={handleGenerateTOTPSecret}
            disabled={isGenerating}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              'Generate New TOTP Secret'
            )}
          </Button>

          {qrCodeUrl && totpSecret && (
            <div className="space-y-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>1. Scan QR Code with Authenticator App</Label>
                <div className="flex justify-center p-4 bg-card border border-border rounded-lg">
                  <img src={qrCodeUrl} alt="TOTP QR Code" className="w-64 h-64" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>2. Manual Entry (if needed)</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-muted p-2 rounded overflow-x-auto">
                    {totpSecret}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(totpSecret, 'TOTP Secret')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>3. Test Code</Label>
                <div className="flex flex-col items-center gap-4">
                  <InputOTP
                    maxLength={6}
                    value={testCode}
                    onChange={setTestCode}
                    onComplete={handleTestTOTP}
                    disabled={isTesting}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>

                  {testResult === 'success' && (
                    <Alert className="bg-success/10 dark:bg-success/20 border-success/30">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                      <AlertDescription className="text-foreground">
                        Code verified successfully! ✓
                      </AlertDescription>
                    </Alert>
                  )}

                  {testResult === 'error' && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Invalid code. Please try again.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    onClick={handleTestTOTP}
                    disabled={testCode.length !== 6 || isTesting}
                    className="w-full"
                  >
                    {isTesting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      'Test Code'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
